<template>
    <div class="gantt-widget">
        <div class="widget-header">
            <div class="left">
                <i
                    class="material-icons drag-handle"
                    v-tippy="{
                        placement: 'top',
                        theme: 'light',
                        arrow: true,
                        animation: 'scale-extreme',
                        duration: [200, 200],
                        hideOnClick: true,
                    }"
                    content="Sürükleyerek taşıyabilirsiniz"
                >
                    drag_indicator
                </i>
                <widget-title :widget="widget" :table="table" @widget-updated="handleWidgetUpdated" />
            </div>

            <div class="header-actions">
                <div class="view-switcher">
                    <a-button-group size="small">
                        <a-button
                            v-for="mode in viewModes"
                            :key="mode.value"
                            :type="currentViewMode === mode.value ? 'primary' : 'default'"
                            @click="changeViewMode(mode.value)"
                        >
                            {{ mode.label }}
                        </a-button>
                    </a-button-group>
                </div>

                <widget-actions
                    @fullscreen="handleFullscreen"
                    @edit="handleEdit"
                    @rename="handleRename"
                    @duplicate="handleDuplicate"
                    @dock="handleDock"
                    @export="handleExport"
                    @delete="$emit('delete', widget._id)"
                    :title="widget.title"
                    :widget="widget"
                    :table="table"
                    @widget-updated="handleWidgetUpdated"
                    @widget-duplicated="handleWidgetDuplicated"
                    @widget-deleted="handleWidgetDeleted"
                />
            </div>
        </div>

        <div class="widget-content">
            <div v-if="!hasValidSettings" class="empty-state">
                <a-empty description="Gantt ayarları yapılmamış">
                    <a-button type="primary" @click="handleEdit">Ayarları Düzenle</a-button>
                </a-empty>
            </div>

            <div v-else-if="loading" class="loading-overlay">
                <loading-wave text="Gantt yükleniyor..." />
            </div>

            <div v-else-if="ganttData && ganttData.error" class="error-state">
                <a-empty :description="ganttData.message || 'Gantt verileri yüklenirken hata oluştu'">
                    <a-button type="primary" @click="handleEdit">Ayarları Kontrol Et</a-button>
                </a-empty>
            </div>

            <div v-else-if="!ganttData || !ganttData.tasks || ganttData.tasks.length === 0" class="empty-state">
                <a-empty description="Gösterilecek görev bulunamadı" />
            </div>

            <div v-else class="gantt-container">
                <div ref="ganttElement" class="gantt-chart"></div>
            </div>
        </div>

        <gantt-settings-modal
            v-if="showSettingsModal"
            :visible="showSettingsModal"
            :widget="widget"
            :fields="table.fields"
            :table="table"
            @close="showSettingsModal = false"
            @save="handleSettingsSave"
        />
    </div>
</template>

<script>
import Vue from "vue";
import { TABLE_API } from "../../../../../api/table";
import log2 from "../../../../../utils/log2";
import WidgetActions from "./charts/components/WidgetActions.vue";
import WidgetTitle from "./charts/components/WidgetTitle.vue";
import GanttSettingsModal from "./gantt/GanttSettingsModal.vue";
import LoadingWave from "../../../../../components/common/LoadingWave.vue";
import "../../../../../assets/css/frappe-gantt.css";
const log = log2("GanttWidget");

// Frappe Gantt'ı dinamik olarak yükle (UMD module import sorunu için)
let Gantt = null;

export default Vue.extend({
    name: "GanttWidget",
    components: {
        WidgetActions,
        WidgetTitle,
        GanttSettingsModal,
        LoadingWave,
    },
    props: {
        widget: {
            type: Object,
            required: true,
        },
        table: {
            type: Object,
            required: true,
        },
        upstreamFilters: {
            type: Object,
            default: () => ({ filters: [], searchQuery: "" }),
        },
    },
    data() {
        return {
            loading: false,
            ganttData: null,
            showSettingsModal: false,
            ganttInstance: null,
            currentViewMode: null,
            viewModes: [
                { label: "Gün", value: "Day" },
                { label: "Hafta", value: "Week" },
                { label: "Ay", value: "Month" },
                { label: "Yıl", value: "Year" },
            ],
        };
    },
    computed: {
        hasValidSettings() {
            return (
                this.widget &&
                this.widget.settings &&
                this.widget.settings.taskNameField &&
                this.widget.settings.startDateField &&
                this.widget.settings.endDateField
            );
        },
    },
    methods: {
        async loadGanttData() {
            if (!this.hasValidSettings) {
                return;
            }

            this.loading = true;
            try {
                const data = await TABLE_API.getGanttData(this.table._id, this.widget._id, this.upstreamFilters);
                this.ganttData = data;

                if (!this.currentViewMode && data.viewMode) {
                    this.currentViewMode = this.mapViewMode(data.viewMode);
                }

                await this.$nextTick();
                this.renderGantt();
            } catch (error) {
                console.error("Gantt verileri yüklenirken hata oluştu", error);
                this.$message.error("Gantt verileri yüklenirken hata oluştu");
            } finally {
                this.loading = false;
            }
        },

        mapViewMode(mode) {
            const mapping = {
                day: "Day",
                week: "Week",
                month: "Month",
                quarter: "Quarter",
                year: "Year",
            };
            return mapping[mode] || "Month";
        },

        changeViewMode(mode) {
            this.currentViewMode = mode;
            if (this.ganttInstance) {
                this.ganttInstance.change_view_mode(mode);
            }
        },

        async renderGantt() {
            if (!this.ganttData || !this.ganttData.tasks || this.ganttData.tasks.length === 0) {
                return;
            }

            // Gantt kütüphanesi yüklenmiş mi kontrol et
            if (!Gantt) {
                console.error("Gantt kütüphanesi henüz yüklenmedi");
                return;
            }

            // Element'in hazır olmasını bekle
            await this.$nextTick();

            if (!this.$refs.ganttElement) {
                console.error("Gantt container element bulunamadı");
                return;
            }

            try {
                // Eğer önceki gantt instance varsa temizle
                if (this.ganttInstance) {
                    this.$refs.ganttElement.innerHTML = "";
                    this.ganttInstance = null;
                }

                // Gantt tasks'ları dönüştür
                const tasks = this.ganttData.tasks.map((task) => {
                    const ganttTask = {
                        id: task.id,
                        name: task.text,
                        start: task.start_date,
                        end: task.end_date,
                        progress: (task.progress || 0) * 100,
                    };

                    // Custom bar sınıfı ekle (renklendirme için)
                    if (task.status && this.ganttData.statusColors && this.ganttData.statusColors[task.status]) {
                        ganttTask.custom_class = `status-${task.status.toLowerCase().replace(/\s+/g, "-")}`;
                    } else if (
                        task.priority &&
                        this.ganttData.priorityColors &&
                        this.ganttData.priorityColors[task.priority]
                    ) {
                        ganttTask.custom_class = `priority-${task.priority.toLowerCase().replace(/\s+/g, "-")}`;
                    }

                    return ganttTask;
                });

                // Gantt instance oluştur
                this.ganttInstance = new Gantt(this.$refs.ganttElement, tasks, {
                    view_mode: this.currentViewMode || "Month",
                    language: "tr",
                    bar_height: 30,
                    bar_corner_radius: 3,
                    arrow_curve: 5,
                    padding: 18,
                    view_modes: ["Quarter Day", "Half Day", "Day", "Week", "Month", "Year"],
                    date_format: "YYYY-MM-DD",
                    custom_popup_html: (task) => {
                        const ganttTask = this.ganttData.tasks.find((t) => t.id === task.id);
                        let html = `
                            <div class="gantt-popup">
                                <h3>${task.name}</h3>
                                <p><strong>Başlangıç:</strong> ${this.$moment(task._start).format("DD.MM.YYYY")}</p>
                                <p><strong>Bitiş:</strong> ${this.$moment(task._end).format("DD.MM.YYYY")}</p>
                                <p><strong>İlerleme:</strong> ${Math.round(task.progress)}%</p>
                        `;

                        if (ganttTask) {
                            if (ganttTask.status) {
                                html += `<p><strong>Durum:</strong> ${ganttTask.status}</p>`;
                            }
                            if (ganttTask.assignee) {
                                html += `<p><strong>Atanan:</strong> ${ganttTask.assignee}</p>`;
                            }
                            if (ganttTask.priority) {
                                html += `<p><strong>Öncelik:</strong> ${ganttTask.priority}</p>`;
                            }
                        }

                        html += `</div>`;
                        return html;
                    },
                });

                // Custom stil ekle
                this.applyCustomStyles();
            } catch (error) {
                console.error("Gantt render hatası", error);
                this.$message.error("Gantt oluşturulurken hata oluştu");
            }
        },

        applyCustomStyles() {
            if (!this.ganttData) return;

            // Status renkleri için stil ekle
            if (this.ganttData.statusColors) {
                const styleId = `gantt-status-colors-${this.widget._id}`;
                let existingStyle = document.getElementById(styleId);
                if (existingStyle) {
                    existingStyle.remove();
                }

                const style = document.createElement("style");
                style.id = styleId;
                let css = "";

                Object.keys(this.ganttData.statusColors).forEach((status) => {
                    const color = this.ganttData.statusColors[status];
                    const className = `status-${status.toLowerCase().replace(/\s+/g, "-")}`;
                    css += `.${className} .bar { fill: ${color} !important; }\n`;
                });

                style.textContent = css;
                document.head.appendChild(style);
            }

            // Priority renkleri için stil ekle
            if (this.ganttData.priorityColors) {
                const styleId = `gantt-priority-colors-${this.widget._id}`;
                let existingStyle = document.getElementById(styleId);
                if (existingStyle) {
                    existingStyle.remove();
                }

                const style = document.createElement("style");
                style.id = styleId;
                let css = "";

                Object.keys(this.ganttData.priorityColors).forEach((priority) => {
                    const color = this.ganttData.priorityColors[priority];
                    const className = `priority-${priority.toLowerCase().replace(/\s+/g, "-")}`;
                    css += `.${className} .bar { fill: ${color} !important; }\n`;
                });

                style.textContent = css;
                document.head.appendChild(style);
            }
        },

        handleEdit() {
            this.showSettingsModal = true;
        },

        handleRename() {
            // Widget title component'i rename'i handle ediyor
        },

        async handleSettingsSave(settings) {
            try {
                await TABLE_API.updateWidget(this.table._id, this.widget.page, this.widget._id, {
                    settings,
                });
                this.$message.success("Gantt ayarları kaydedildi");
                this.showSettingsModal = false;

                // Widget'ı güncelle
                this.widget.settings = settings;
                this.$emit("widget-updated", this.widget);

                // Gantt'ı yeniden yükle
                await this.loadGanttData();
            } catch (error) {
                console.error("Gantt ayarları kaydedilirken hata oluştu", error);
                this.$message.error("Gantt ayarları kaydedilirken hata oluştu");
            }
        },

        handleWidgetUpdated(updatedWidget) {
            this.$emit("widget-updated", updatedWidget);
        },

        async handleDuplicate() {
            try {
                const result = await TABLE_API.duplicateWidget(this.table._id, this.widget.page, this.widget._id);
                this.$message.success("Widget kopyalandı");
                this.$emit("widget-duplicated", result);
            } catch (error) {
                console.error("Widget kopyalanırken hata oluştu", error);
                this.$message.error("Widget kopyalanırken hata oluştu");
            }
        },

        handleWidgetDuplicated(result) {
            this.$emit("widget-duplicated", result);
        },

        handleWidgetDeleted() {
            this.$emit("delete", this.widget._id);
        },

        handleFullscreen() {
            // TODO: Fullscreen implementasyonu
            this.$message.info("Fullscreen yakında eklenecek");
        },

        handleDock() {
            // TODO: Dock implementasyonu
            this.$message.info("Dock yakında eklenecek");
        },

        handleExport() {
            // TODO: Export implementasyonu
            this.$message.info("Export yakında eklenecek");
        },
    },

    async mounted() {
        // Frappe Gantt'ı dinamik olarak import et (constructor hatası önleme)
        try {
            const FrappeGanttModule = await import("frappe-gantt");

            // Farklı export türlerini dene
            if (FrappeGanttModule.default) {
                Gantt = FrappeGanttModule.default;
            } else if (FrappeGanttModule.Gantt) {
                Gantt = FrappeGanttModule.Gantt;
            } else if (typeof FrappeGanttModule === "function") {
                Gantt = FrappeGanttModule;
            } else {
                // Son çare: window.Gantt'ı kullan (UMD build için)
                Gantt = window.Gantt;
            }

            if (!Gantt || typeof Gantt !== "function") {
                throw new Error("Gantt constructor bulunamadı");
            }

            log("Frappe Gantt başarıyla yüklendi", Gantt);
        } catch (error) {
            console.error("Frappe Gantt yüklenemedi:", error);
            this.$message.error("Gantt kütüphanesi yüklenemedi");
            return;
        }

        await this.loadGanttData();
    },

    beforeDestroy() {
        // Gantt instance'ı temizle
        if (this.ganttInstance) {
            if (this.$refs.ganttElement) {
                this.$refs.ganttElement.innerHTML = "";
            }
            this.ganttInstance = null;
        }

        // Custom stilleri temizle
        const statusStyleId = `gantt-status-colors-${this.widget._id}`;
        const priorityStyleId = `gantt-priority-colors-${this.widget._id}`;

        const statusStyle = document.getElementById(statusStyleId);
        if (statusStyle) {
            statusStyle.remove();
        }

        const priorityStyle = document.getElementById(priorityStyleId);
        if (priorityStyle) {
            priorityStyle.remove();
        }
    },

    watch: {
        upstreamFilters: {
            handler() {
                this.loadGanttData();
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.gantt-widget {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #fff;

    .widget-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #e8e8e8;
        background: #fafafa;

        .left {
            display: flex;
            align-items: center;
            gap: 8px;

            .drag-handle {
                cursor: grab;
                color: #999;
                font-size: 20px;

                &:active {
                    cursor: grabbing;
                }
            }
        }

        .header-actions {
            display: flex;
            align-items: center;
            gap: 10px;

            .view-switcher {
                margin-right: 10px;
            }
        }
    }

    .widget-content {
        flex: 1;
        overflow: auto;
        position: relative;

        .empty-state,
        .error-state {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
            min-height: 300px;
        }

        .loading-overlay {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
            min-height: 300px;
        }

        .gantt-container {
            padding: 20px;
            overflow-x: auto;

            .gantt-chart {
                width: 100%;
                height: auto;
                min-height: 400px;
            }
        }
    }
}
</style>

<style lang="scss">
// Gantt global stilleri
.gantt-popup {
    padding: 10px;

    h3 {
        margin: 0 0 10px 0;
        font-size: 14px;
        font-weight: 600;
    }

    p {
        margin: 5px 0;
        font-size: 12px;

        strong {
            font-weight: 600;
        }
    }
}

// Frappe Gantt özelleştirmeleri
.gantt {
    .bar-wrapper {
        cursor: pointer;

        &:hover {
            .bar {
                opacity: 0.9;
            }
        }
    }

    .bar-progress {
        fill: rgba(255, 255, 255, 0.3);
    }
}
</style>
